import {
  Button,
  Edit,
  Link,
  Menu,
  ThemeProvider,
  Tooltip,
} from '@sparemin/blockhead';
import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import { hideModal } from 'redux/modules/modal';
import { getProjectPath } from 'utils/routes';
import {
  useEddyProjectId,
  useIsEditable,
  useIsEditDisabled,
  useIsShort,
  useIsTranscriptEditable,
  useProjectPreviewModalContext,
  useProjectWidgetId,
} from './ProjectPreviewModalContext';
import { EditItem, EditOption } from './types';
import { block } from './utils';

export interface ProjectPreviewModalEditOptionsProps {}

const ProjectPreviewModalEditOptions: React.FC<ProjectPreviewModalEditOptionsProps> = () => {
  const {
    projectId,
    embedConfig,
    isUCSCompatible,
  } = useProjectPreviewModalContext();
  const isShort = useIsShort();
  const isTranscriptEditable = useIsTranscriptEditable();
  const isEditable = useIsEditable();
  const eddyProjectId = useEddyProjectId();
  const widgetId = useProjectWidgetId();
  const isEditDisabled = useIsEditDisabled();

  const dispatch = useDispatch();
  const location = useLocation();

  const projectPathname = getProjectPath(projectId, {
    source: location?.pathname,
  })?.pathname;

  const disabledKeys = useMemo((): EditOption[] => {
    const disabledEditOptions: EditOption[] = [];

    if (!isTranscriptEditable) {
      disabledEditOptions.push('edit-transcript');
    }

    if (!isUCSCompatible || !embedConfig) {
      disabledEditOptions.push('edit-template');
    }

    if (!isShort || !isEditable) {
      disabledEditOptions.push('open-advanced-editor');
    }

    return disabledEditOptions;
  }, [embedConfig, isEditable, isShort, isTranscriptEditable, isUCSCompatible]);

  const editItems = useMemo((): EditItem[] => {
    const items: EditItem[] = [];

    if (!isShort) {
      items.push({
        key: 'edit-transcript',
        label: 'Edit transcript',
        tooltip: !isTranscriptEditable
          ? 'No transcript exists for this project'
          : undefined,
      });
    }

    items.push(
      {
        key: 'edit-template',
        label: 'Open in Quick Editor',
        tooltip: !isUCSCompatible
          ? 'Open this project in the Advanced Editor to make changes'
          : undefined,
      },
      {
        key: 'open-advanced-editor',
        label: 'Open in Advanced Editor',
        pathname: projectPathname,
        tooltip: !isShort
          ? 'Not available for projects greater than 10 minutes'
          : !isEditable
          ? 'This project is not compatible with the Advanced Editor'
          : undefined,
      },
    );

    return items;
  }, [
    isEditable,
    isShort,
    isTranscriptEditable,
    isUCSCompatible,
    projectPathname,
  ]);

  const handleSelectEditAction = useCallback(
    (action: EditOption): void => {
      if (action === 'edit-transcript') {
        dispatch(
          hideModal({
            action: 'EddyEditTranscript',
            eddyProjectId,
            embedConfig,
          }),
        );
      }

      if (action === 'edit-template') {
        dispatch(
          hideModal({
            action: 'EditTemplate',
            projectId,
            widgetId,
            embedConfig,
            isShortFormProject: isShort,
          }),
        );
      }

      if (action === 'open-advanced-editor') {
        window.open(projectPathname, '_blank');
      }
    },
    [
      dispatch,
      eddyProjectId,
      embedConfig,
      isShort,
      projectId,
      projectPathname,
      widgetId,
    ],
  );

  return (
    <ThemeProvider theme="flip">
      <Menu
        button={
          <Button
            color="secondary"
            startIcon={<Edit />}
            isDisabled={isEditDisabled}
          >
            Edit
          </Button>
        }
        offset={10}
        density="compact"
        placement="top"
        disabledKeys={disabledKeys}
        onAction={handleSelectEditAction}
      >
        {editItems.map(({ key, label, pathname, tooltip }) => {
          const isDisabled = disabledKeys.includes(key);
          const isLink = pathname && !isDisabled;
          const Component = isLink ? Menu.LinkItem : Menu.Item;

          return (
            <Component
              key={key}
              {...(isLink && {
                href: pathname,
                target: '_blank',
                variant: 'inherit',
                linkComponent: Link,
                inheritColor: true,
              })}
            >
              <div className={block('edit-item-container')}>
                <Tooltip arrow contents={tooltip} isDisabled={!tooltip}>
                  <span
                    className={block('edit-item', {
                      disabled: isDisabled,
                    })}
                  >
                    {label}
                  </span>
                </Tooltip>
              </div>
            </Component>
          );
        })}
      </Menu>
    </ThemeProvider>
  );
};

export default ProjectPreviewModalEditOptions;
